import { useCallback, useMemo, useRef } from 'react';
import {
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useNoteTakerConfig,
  useDurationFieldConfig,
  useMeetingRoomFieldConfig,
} from '@/formConfigs';
import { useInterviewerField } from '../useInterviewerField';
import { buildScheduleHostField } from '../useScheduleHostField';
import { isHostOptionDisabled } from '../../fields/HostRenders';
import { resolveLockedHostId } from '../../helpers/resolveLockedHostId';
import type { ScheduleMode, ApiReference } from '../../../../config/types';
import {
  isAdhocMode,
  isBulkSelfScheduleMode,
  isManualMode,
  isSelfScheduleMode,
} from '../../../../config/modeClassifiers';
import { useHackerRankTemplates } from '../../../../../InterviewActivity/details/useHackerRankTemplates';
import { useIntegrations } from '@/hooks/use-integrations';
import { useSuggestedInterviewer } from '@/api/suggestedInterviewer';
import { useScheduleCalendarData } from '../useScheduleCalendarData';
import { useTimeOptions } from './useTimeOptions';
import { buildInterviewDetailsSchema } from './interviewDetailsSchema';
import { buildScheduleDetailsSchema } from './scheduleDetailsSchema';
import {
  renderMeetingRoomOption,
  renderMeetingRoomTag,
  renderMeetingRoomTriggerValue,
} from '../../fields/MeetingRoomRenders';
import { copy } from './constants';
import type { UseDetailsFormSchemaOptions } from './types';

export function useDetailsFormSchema(
  modeOrOptions: ScheduleMode | UseDetailsFormSchemaOptions,
  apiReferenceArg?: ApiReference
) {
  // Support both old call signature (mode, apiReference) and options object.
  const options: UseDetailsFormSchemaOptions =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions, apiReference: apiReferenceArg }
      : modeOrOptions;

  const {
    mode,
    apiReference = 'create',
    interviewPlan,
    noteTakerMandatory,
    interviewPlatform,
    meetingRoomsEnabled = false,
    interviewType = 'one_on_one',
    selectedStartDate = null,
    selectedStartTime = '',
    selectedEndTime = '',
    calendarInfo,
    scheduleTimezone,
    calendarBookedStartIso = null,
    calendarBookedEndIso = null,
    visibleWeekStartIso = null,
    disconnectedInterviewerIds,
    meetingRoomGroups,
    meetingRoomsLoading = false,
    candidateName,
    candidateAvatar,
    candidatePhone,
    atsJobStageActionId,
    hasInterviewerSelected = false,
    participantDefaultOptions,
    hostParticipantOptions,
    currentUserHostInfo,
    loggedInUserId,
    editSavedHost,
    currentHostId,
    findTimeProps,
    bufferProps,
    onFieldChange,
    onInterviewerRecommend,
    onSetTimezone,
  } = options;

  // Keep `onFieldChange` out of the schema's memo deps via a ref —
  // `handleFieldChange` is recreated whenever `bookedSlot` changes, so
  // depending on it directly rebuilt the entire schema (and re-rendered
  // every FormBuilder field) on each slot update, making the edit drawer
  // open slowly. The ref always points at the latest handler while the
  // wrapper identity stays stable.
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;
  const stableOnFieldChange = useCallback(
    (name: string, value: unknown, allValues: Record<string, unknown>) =>
      onFieldChangeRef.current?.(name, value, allValues),
    []
  );

  const scheduleTz =
    scheduleTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── Mode flags ──────────────────────────────────────────────────────────
  const isManualOrAdhoc = isManualMode(mode);
  const isSelfSchedule = isSelfScheduleMode(mode);
  const isBulkSelfSchedule = isBulkSelfScheduleMode(mode);
  const isAdhoc = isAdhocMode(mode);
  const isEdit = apiReference === 'edit';
  const isMultiInterviewer = isSelfSchedule || interviewType === 'panel';

  // HackerRank: plan carries location='hackerrank' for HR interviews.
  // `isHackerRank` drives the location-field disable so the recruiter
  // can't switch the interview to another location — same gate as
  // legacy `useSchLocationField`. Two entry points feed it:
  //   - plan-driven: the interview plan saved `location: 'hackerrank'`
  //   - adhoc-driven: launched from the "Send HackerRank interview"
  //     candidate-action menu entry (interviewPlatform === 'hackerrank')
  const isHackerRank =
    (interviewPlan?.location as string | undefined) === 'hackerrank' ||
    interviewPlatform === 'hackerrank';

  // Templates are fetched whenever the workspace has the HackerRank
  // integration enabled — NOT only when the plan saved
  // `location: hackerrank`. The previous gate broke adhoc create + any
  // plan-create where the recruiter manually picked HackerRank from a
  // non-HR plan: the field rendered (gated on the live `location` form
  // value) but its options were always empty. Mirrors
  // `useInterviewActivityDetailsSchema` and the legacy fetch which had
  // no gate at all.
  const { isHackerRankEnabled } = useIntegrations() as {
    isHackerRankEnabled?: boolean;
  };

  // ── Sub-hooks ────────────────────────────────────────────────────────────
  const {
    templateOptions: hackerRankTemplateOptions,
    isLoading: hrLoading,
    userHasNoAccount: hrUserHasNoAccount,
  } = useHackerRankTemplates({ enabled: Boolean(isHackerRankEnabled) });

  // AI-recommended interviewer — manual one-on-one create from plan only.
  // Fires immediately on mount (no slot required). Uses stable placeholder
  // times via ref so the query key doesn't change every render.
  //
  // Stops firing once the user has picked an interviewer — at that point
  // the recommendation pill is hidden and the query was the only thing
  // still refetching as `calendarBookedStartIso`/`endIso` (slot drag,
  // resize) or other deps churned. Without this gate the network tab
  // shows a fresh `/recommend` call every time the user moves the booked
  // slot even though nothing is consuming the result.
  const hasBookedSlot = Boolean(calendarBookedStartIso && calendarBookedEndIso);
  const canRecommend =
    !isEdit &&
    mode === 'manual' &&
    !isAdhoc &&
    interviewType === 'one_on_one' &&
    typeof atsJobStageActionId === 'number' &&
    atsJobStageActionId > 0 &&
    !hasInterviewerSelected;

  const placeholderTimesRef = useRef<{ start: string; end: string } | null>(
    null
  );
  if (!placeholderTimesRef.current) {
    const now = Date.now();
    placeholderTimesRef.current = {
      start: new Date(now).toISOString(),
      end: new Date(now + 30 * 60 * 1000).toISOString(),
    };
  }
  const recommendStartAt =
    calendarBookedStartIso ?? placeholderTimesRef.current.start;
  const recommendEndAt =
    calendarBookedEndIso ?? placeholderTimesRef.current.end;

  const { data: recommendedUser, isLoading: isRecommending } =
    useSuggestedInterviewer({
      enabled: canRecommend,
      atsJobStageActionId: atsJobStageActionId ?? 0,
      startAt: recommendStartAt,
      endAt: recommendEndAt,
      isTimeslot: hasBookedSlot,
      timeZone: scheduleTz,
    });

  const calendarData = useScheduleCalendarData({
    interviewPlan,
    apiReference,
    calendarInfo,
    isAdhoc,
    isSelfSchedule,
  });
  const shouldShowCalendarRow = calendarData.shouldShowRow;

  // Pool-group dropdown: split plan-configured candidates ("SELECTED")
  // from the general user list whenever we're scheduling from a plan
  // (any mode — manual, panel, self-schedule). The actual split is
  // done in `loadOptions` at runtime (only activates when the pool is
  // non-empty), so adhoc and pool-less plans fall through to the flat
  // single-group layout. Gated on a real plan stage action id so adhoc
  // (no plan) stays in the single-group layout.
  const showPoolGroup =
    !isAdhoc &&
    typeof atsJobStageActionId === 'number' &&
    atsJobStageActionId > 0;

  const interviewerField = useInterviewerField({
    label: isMultiInterviewer
      ? copy.interviewer.labelPlural
      : copy.interviewer.label,
    placeholder: copy.interviewer.placeholder,
    isMulti: isMultiInterviewer,
    defaultOptions: participantDefaultOptions,
    calendarBookedStartIso,
    scheduleTimezone: scheduleTz,
    atsJobStageActionId,
    visibleWeekStartIso,
    disconnectedInterviewerIds,
    showPoolGroup,
    onSetTimezone,
    showSetTimezone: isSelfSchedule,
    // "You" — the real logged-in user, NOT `currentUserHostInfo` (which is
    // the interview creator in edit mode). Hides the set-timezone globe on
    // your own interviewer chip.
    currentUserId:
      loggedInUserId !== undefined ? Number(loggedInUserId) : undefined,
    // Outlook: when the picked shared-calendar's owner is in the
    // interviewer list, their chip surfaces the "you won't get an
    // invite" warning. `outlookOwner` is null only when the user isn't
    // on Outlook. The primary-calendar case is suppressed HERE (legacy
    // `!calendar_info?.is_primary` chip gate) — NOT by nulling the
    // owner, so the organizer info icon still shows for primary.
    outlookCalendarOwnerUserId: calendarData.isPrimarySelected
      ? null
      : (calendarData.outlookOwner?.userId ?? null),
  });

  // Zoom can't be hosted if every candidate host (picked interviewers +
  // recruiter + edit-mode saved host) is marked "Not part of Zoom" /
  // rate-limited. Computed here and forwarded to the location field so
  // the Zoom option disables itself; otherwise the recruiter picks Zoom
  // and lands on an empty Host dropdown.
  const zoomHasNoEligibleHost = useMemo(() => {
    const candidates = [
      ...(hostParticipantOptions ?? []),
      ...(currentUserHostInfo ? [currentUserHostInfo] : []),
      ...(editSavedHost ? [editSavedHost] : []),
    ];
    if (candidates.length === 0) return false;
    return candidates.every((c) => isHostOptionDisabled(c.zoomStatus));
  }, [hostParticipantOptions, currentUserHostInfo, editSavedHost]);

  const { fieldConfig: locationField } = useLocationFieldConfig({
    isHackerRank,
    hackerRankUserHasNoAccount: hrUserHasNoAccount,
    zoomHasNoEligibleHost,
    config: {
      label: copy.location.label,
      required: true,
      condition: {
        operator: 'isNotEmpty',
        field: 'participant_ids',
        preserveValue: true,
      },
    },
  });

  const { fieldConfig: officeField } = useOfficeFieldConfig({
    config: { name: 'ats_office_id' },
  });

  // Lock the AI-notetaker toggle off the FRESH per-stage mandate carried by
  // the plan/edit response (`interviewPlan.note_taker_mandatory`), not the
  // shared `atsJobCreate.currentJob` slice the wizard never refetches (which
  // can stay `true` after the account mandate master is switched off). Adhoc
  // has no `interviewPlan` but its plan IS fetched, so the fresh mandate
  // arrives via `noteTakerMandatory` — used here so the toggle locks for
  // adhoc instead of the never-populated `currentJob` slice (ENG-3339).
  // Stays `undefined` until the plan lands → the hook keeps its currentJob
  // fallback in the meantime.
  const { field: noteTakerField } = useNoteTakerConfig({
    mandatory: interviewPlan
      ? Boolean(interviewPlan.note_taker_mandatory)
      : noteTakerMandatory,
  });

  const { fieldConfig: durationField } = useDurationFieldConfig({
    name: 'duration',
    label: copy.duration.label,
    unitLabel: copy.duration.unit,
  });

  // Host (Zoom) — replaces the old generic account-user picker. Options
  // are drawn from the picked interviewers + the current user (legacy
  // `SchZoomHost.tsx` constraint) and each option carries the user's
  // Zoom-hosting status so the chip + dropdown row paint Paid / Free /
  // Needs-license / etc.
  //
  // SCH-37 (product call): self-schedule one_on_one with MULTIPLE
  // picked interviewers locks Host to the resolved host — the actual
  // interviewer isn't decided until the candidate picks a slot, so
  // offering a specific interviewer-host is misleading UX. With ONE
  // interviewer the picked attendee IS known, so the recruiter can
  // choose between that interviewer and themselves (legacy
  // `SchZoomHost` `hostOptions.length > 1` gate parity). BE
  // `validate_host` accepts any of `{current_user, picked_interviewers}`.
  const hostLocked =
    isSelfSchedule &&
    interviewType === 'one_on_one' &&
    (hostParticipantOptions?.length ?? 0) > 1;

  // Non-locked prefill target: first selectable Host option, in dropdown
  // order (interviewers → organizer). Used to auto-fill Host the moment
  // Zoom is chosen in CREATE so the recruiter isn't left on a blank
  // required field. `isHostOptionDisabled` = the same "can host"
  // predicate the option rows + Location gate use.
  const firstAvailableHostId = (() => {
    const fromParticipants = (hostParticipantOptions ?? []).find(
      (o) => o.value && !isHostOptionDisabled(o.zoomStatus)
    );
    if (fromParticipants) return String(fromParticipants.value);
    if (
      currentUserHostInfo?.id !== undefined &&
      !isHostOptionDisabled(currentUserHostInfo.zoomStatus)
    ) {
      return String(currentUserHostInfo.id);
    }
    return '';
  })();

  // Resolved locked host: organizer → else first valid interviewer →
  // else '' (nobody can host Zoom → Zoom gets disabled/falls back via
  // `zoomHasNoEligibleHost`). Single source of truth shared by the field
  // `defaultValue` and the lock effect below.
  const lockedHostId = hostLocked
    ? resolveLockedHostId({
        organizer:
          currentUserHostInfo?.id !== undefined
            ? {
                id: currentUserHostInfo.id,
                zoomStatus: currentUserHostInfo.zoomStatus,
              }
            : undefined,
        participantOptions: hostParticipantOptions ?? [],
      })
    : '';

  // Enforcer #1 — FormBuilder schema effect for FORM-DRIVEN changes RHF
  // can see: `location`/`participant_ids`/`interview_type`, incl.
  // programmatic `setValue` and re-asserting after FormBuilder's
  // conditional-field reset blanks `host_id` on a Location toggle. The
  // broken `sharedState.location` mirror is irrelevant (RHF `useWatch`).
  //
  // CRITICAL: the effect object MUST keep a stable identity. FormBuilder's
  // `FieldEffect` skips its FIRST run via an `isFirst` ref; if the object
  // identity churned (deps on hostLocked/lockedHostId) FormBuilder would
  // remount it, resetting `isFirst` and swallowing the very change we
  // need. So memoize once (`[]`) and read the latest resolved values from
  // a ref. Mount / async-load is owned by enforcer #2 in DetailsStep
  // (`FieldEffect` deliberately skips mount).
  const isCreate = apiReference !== 'edit';
  const lockRef = useRef({
    hostLocked,
    lockedHostId,
    firstAvailableHostId,
    isCreate,
  });
  lockRef.current = {
    hostLocked,
    lockedHostId,
    firstAvailableHostId,
    isCreate,
  };
  const hostLockEffect = useRef({
    type: 'effect' as const,
    watch: ['location', 'participant_ids', 'interview_type'],
    handler: (
      watched: Record<string, unknown>,
      methods: {
        getValues: (_n?: string) => unknown;
        setValue: (_n: string, _v: unknown, _o?: unknown) => void;
      }
    ) => {
      if (watched.location !== 'zoom') return;
      const {
        hostLocked: locked,
        lockedHostId: target,
        firstAvailableHostId: firstAvail,
        isCreate: create,
      } = lockRef.current;
      const cur = methods.getValues('host_id');
      if (locked) {
        if (!target || cur === target) return;
        methods.setValue('host_id', target, {
          shouldValidate: true,
          shouldDirty: true,
        });
        return;
      }
      // Non-locked: when Zoom is just selected in CREATE and Host is
      // still empty, auto-fill the first available option. Empty-only so
      // it never clobbers a user pick or the `pickAutoHostId` effect.
      if (create && !cur && firstAvail) {
        methods.setValue('host_id', firstAvail, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
  }).current;

  const hostField = useMemo(
    () =>
      buildScheduleHostField({
        disabled: hostLocked,
        // Only currently-picked interviewers — NOT the plan pool. The
        // pool sits in `participantDefaultOptions` (used by the
        // interviewer async-select for chip rendering); routing it
        // through here would let the Host dropdown propose users who
        // aren't actually attending the interview, which legacy
        // `SchZoomHost` explicitly forbade.
        participantOptions: hostParticipantOptions ?? [],
        currentUser: currentUserHostInfo,
        savedHost: editSavedHost,
        currentHostId,
        // Host appears only after Location appears, which itself is gated
        // on at least one interviewer being picked. Without the
        // `isNotEmpty(participant_ids)` precondition, the Host field
        // showed in adhoc create even before an interviewer was picked
        // (Location stayed hidden by its own condition, but Host
        // matched on the form's default `location: 'zoom'` and rendered
        // out of order).
        condition: {
          operator: 'and',
          conditions: [
            {
              operator: 'isNotEmpty',
              field: 'participant_ids',
            },
            {
              operator: 'equal',
              field: 'location',
              value: 'zoom',
            },
          ],
        },
      }),
    [
      hostParticipantOptions,
      currentUserHostInfo,
      editSavedHost,
      currentHostId,
      hostLocked,
    ]
  );

  // Status (Available / Busy) is meaningful only when scored against a
  // specific slot. Strip it when:
  //   - Self-schedule: candidate picks the time, multiple slots possible.
  //   - Manual without a slot picked yet: `useMeetingRoomGroupedOptions`
  //     falls back to a 9-hour today-window for the API call so rooms
  //     load eagerly, but a room being busy SOMEWHERE in those 9 hours
  //     doesn't tell the user anything about the slot they'll eventually
  //     pick. Showing "Busy" here is misleading.
  const stripRoomStatus = isSelfSchedule || !calendarBookedStartIso;
  const meetingRoomGroupsForMode = useMemo(
    () =>
      stripRoomStatus
        ? meetingRoomGroups.map((group) => ({
            ...group,
            options: group.options.map((opt) => ({
              ...opt,
              data: {
                ...(opt.data as Record<string, unknown>),
                status: undefined,
              },
            })),
          }))
        : meetingRoomGroups,
    [stripRoomStatus, meetingRoomGroups]
  );

  const { fieldConfig: meetingRoomField } = useMeetingRoomFieldConfig({
    name: 'meeting_room_ids',
    label: copy.meetingRoom.label,
    isMulti: false,
    placeholder: 'Search meeting rooms',
    // Required only while the "Book a meeting room" checkbox is on —
    // FormBuilder gates the required check on the same `condition`.
    required: copy.meetingRoom.requiredError,
    condition: {
      operator: 'equal',
      field: 'book_meeting_room',
      value: true,
    },
    groupedOptions: meetingRoomGroupsForMode,
    loading: meetingRoomsLoading,
    showOptionDividers: true,
    renderOption: renderMeetingRoomOption,
    renderTriggerValue: renderMeetingRoomTriggerValue,
    renderTag: renderMeetingRoomTag,
  });

  const { startTimeOptions, endTimeOptions, datePickerMinDate } =
    useTimeOptions({
      selectedStartDate,
      selectedStartTime,
      selectedEndTime,
      calendarBookedStartIso,
      calendarBookedEndIso,
      scheduleTz,
    });

  // End-date picker floor — End date only renders for a multi-day slot
  // (ends on a LATER day), so disable the start day and everything
  // before it (legacy's `disabled: { before: startDate + 1 day }`).
  // Falls back to the shared min (today) before a start date is picked.
  const endDatePickerMinDate = useMemo(() => {
    if (!selectedStartDate) return datePickerMinDate;
    const d = new Date(selectedStartDate);
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [selectedStartDate, datePickerMinDate]);

  // ── Schema composition ────────────────────────────────────────────────────
  return useMemo(
    () => [
      ...buildInterviewDetailsSchema({
        interviewPlan,
        interviewerField,
        meetingRoomField,
        meetingRoomsEnabled,
        isMultiInterviewer,
        isSelfSchedule,
        canRecommend,
        isRecommending,
        recommendedUser,
        onInterviewerRecommend,
      }),
      ...buildScheduleDetailsSchema({
        isManualOrAdhoc,
        isSelfSchedule,
        isBulkSelfSchedule,
        apiReference,
        calendarData,
        shouldShowCalendarRow,
        durationField,
        locationField,
        hostField,
        officeField,
        noteTakerField,
        hackerRankTemplateOptions,
        hrLoading,
        startTimeOptions,
        endTimeOptions,
        datePickerMinDate,
        endDatePickerMinDate,
        onDateFieldChange: stableOnFieldChange,
        candidateName,
        candidateAvatar,
        candidatePhone,
        findTimeProps,
        bufferProps,
        scheduleTimezone,
      }),
      hostLockEffect,
    ],
    [
      interviewPlan,
      meetingRoomsEnabled,
      meetingRoomField,
      interviewerField,
      durationField,
      isManualOrAdhoc,
      datePickerMinDate,
      endDatePickerMinDate,
      startTimeOptions,
      endTimeOptions,
      locationField,
      hostField,
      officeField,
      noteTakerField,
      isMultiInterviewer,
      isSelfSchedule,
      isBulkSelfSchedule,
      apiReference,
      hackerRankTemplateOptions,
      hrLoading,
      candidateName,
      candidateAvatar,
      candidatePhone,
      canRecommend,
      isRecommending,
      recommendedUser,
      onInterviewerRecommend,
      shouldShowCalendarRow,
      calendarData,
      findTimeProps,
      bufferProps,
      scheduleTimezone,
      stableOnFieldChange,
      hostLockEffect,
    ]
  );
}
